/**
 * Manejador global de errores que filtra errores de múltiples instancias
 */

// Lista de errores conocidos que deben ser silenciados o filtrados
const FILTERED_ERRORS = [
  'Could not establish connection',
  'Receiving end does not exist',
  'Extension context invalidated',
  'Cannot access contents of',
  'Script error',
  'ResizeObserver loop limit exceeded',
  'Non-Error promise rejection captured'
];

// Lista de errores de red que son esperables
const NETWORK_ERRORS = [
  'Failed to fetch',
  'NetworkError',
  'TypeError: Load failed',
  'AbortError'
];

class ErrorHandler {
  constructor() {
    this.setupGlobalErrorHandling();
    this.errorCounts = new Map();
    this.maxErrorsPerType = 5; // Máximo de errores del mismo tipo por minuto
    this.errorWindow = 60000; // 1 minuto
  }

  setupGlobalErrorHandling() {
    // Manejar errores no capturados
    window.addEventListener('error', this.handleError.bind(this));
    
    // Manejar promesas rechazadas
    window.addEventListener('unhandledrejection', this.handleRejection.bind(this));
    
    // Interceptar console.error para filtrar
    this.setupConsoleFiltering();
  }

  handleError(event) {
    const { message, filename, lineno, colno, error } = event;
    
    // Filtrar errores conocidos de múltiples instancias
    if (this.shouldFilterError(message)) {
      event.preventDefault();
      return false;
    }

    // Log del error si no está filtrado
    if (process.env.NODE_ENV === 'development') {
      console.error('Global error caught:', {
        message,
        filename,
        lineno,
        colno,
        error
      });
    }
  }

  handleRejection(event) {
    const reason = event.reason;
    let message = '';
    
    if (reason instanceof Error) {
      message = reason.message;
    } else if (typeof reason === 'string') {
      message = reason;
    } else {
      message = JSON.stringify(reason);
    }

    // Filtrar promesas rechazadas conocidas
    if (this.shouldFilterError(message)) {
      event.preventDefault();
      return false;
    }

    // Log del rechazo si no está filtrado
    if (process.env.NODE_ENV === 'development') {
      console.error('Unhandled promise rejection:', reason);
    }
  }

  shouldFilterError(message) {
    if (!message || typeof message !== 'string') {
      return false;
    }

    // Filtrar errores de múltiples instancias
    const isFiltered = FILTERED_ERRORS.some(error => 
      message.includes(error)
    );

    // Limitar errores repetitivos
    if (this.isErrorRateLimited(message)) {
      return true;
    }

    return isFiltered;
  }

  isErrorRateLimited(message) {
    const errorKey = this.getErrorKey(message);
    const now = Date.now();
    
    if (!this.errorCounts.has(errorKey)) {
      this.errorCounts.set(errorKey, []);
    }

    const errorTimes = this.errorCounts.get(errorKey);
    
    // Limpiar errores antiguos
    const recentErrors = errorTimes.filter(time => 
      now - time < this.errorWindow
    );
    
    this.errorCounts.set(errorKey, recentErrors);

    // Si hay demasiados errores del mismo tipo, limitarlos
    if (recentErrors.length >= this.maxErrorsPerType) {
      return true;
    }

    // Agregar este error al contador
    recentErrors.push(now);
    
    return false;
  }

  getErrorKey(message) {
    // Crear una clave única para el tipo de error
    if (message.includes('Could not establish connection')) {
      return 'connection_error';
    }
    if (message.includes('Receiving end does not exist')) {
      return 'receiving_end_error';
    }
    if (message.includes('Failed to fetch')) {
      return 'fetch_error';
    }
    
    // Para otros errores, usar los primeros 50 caracteres
    return message.substring(0, 50);
  }

  setupConsoleFiltering() {
    const originalError = console.error;
    const originalWarn = console.warn;
    
    console.error = (...args) => {
      const message = args.join(' ');
      
      if (!this.shouldFilterError(message)) {
        originalError.apply(console, args);
      } else if (process.env.NODE_ENV === 'development') {
        // En desarrollo, mostrar errores filtrados con prefix
        originalError.apply(console, ['[FILTERED]', ...args]);
      }
    };

    console.warn = (...args) => {
      const message = args.join(' ');
      
      if (!this.shouldFilterError(message)) {
        originalWarn.apply(console, args);
      }
    };
  }

  // Método para reportar errores importantes manualmente
  reportError(error, context = {}) {
    if (process.env.NODE_ENV === 'production') {
      // Aquí podrías enviar a un servicio de monitoreo como Sentry
      console.error('Error reported:', error, context);
    } else {
      console.error('Development error:', error, context);
    }
  }

  // Limpiar contadores periódicamente
  cleanup() {
    const now = Date.now();
    this.errorCounts.forEach((times, key) => {
      const recentTimes = times.filter(time => now - time < this.errorWindow);
      if (recentTimes.length === 0) {
        this.errorCounts.delete(key);
      } else {
        this.errorCounts.set(key, recentTimes);
      }
    });
  }

  // Obtener estadísticas de errores (para debugging)
  getErrorStats() {
    const stats = {};
    this.errorCounts.forEach((times, key) => {
      stats[key] = times.length;
    });
    return stats;
  }
}

// Crear instancia global del manejador de errores
const errorHandler = new ErrorHandler();

// Limpiar contadores cada minuto
setInterval(() => {
  errorHandler.cleanup();
}, 60000);

export default errorHandler;